import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Button from "../other/Button";
import {
  User,
  AlertCircle,
  Copy,
  ThumbsUp,
  ThumbsDown,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  FileText,
  Eye,
  Download,
  FileSpreadsheet,
  Upload,
  Check,
  ExternalLink,
  Pencil,
  X,
} from "lucide-react";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import ProgressPanel from "./progresspanel";
import DashboardModal from "./DashboardModal";
import { saveReportsToDrive } from "../../utils/googleDrive";


// Empty string = same-origin (LB routes /api/* to the backend) so cookies stay first-party.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";


// --- CUSTOM AI SPARKLE ICON ---
// --- CUSTOM AI SPARKLE ICON (SOLID NEON BLUE & WHITE) ---
const AISparkleIcon = ({ className = "w-6 h-6", darkMode = true }) => {
  // The dark variant's white-topped glow gradient washes out to near-invisible
  // on a light background, so light mode gets a solid saturated gradient and
  // a soft drop shadow instead of a glow.
  const gradientId = darkMode ? "ai-neon-solid" : "ai-neon-solid-light";
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{
        filter: darkMode
          ? "drop-shadow(0 0 6px rgba(6, 182, 212, 0.9))"
          : "drop-shadow(0 1px 2px rgba(8, 145, 178, 0.35))",
      }}
    >
      <defs>
        {darkMode ? (
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#ffffff" />   {/* Pure White top */}
            <stop offset="40%" stopColor="#cffafe" />  {/* Icy middle */}
            <stop offset="100%" stopColor="#06b6d4" /> {/* Cyan bottom */}
          </linearGradient>
        ) : (
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#0369a1" />   {/* Deep sky blue top */}
            <stop offset="45%" stopColor="#0891b2" />  {/* Teal middle */}
            <stop offset="100%" stopColor="#06b6d4" /> {/* Cyan bottom */}
          </linearGradient>
        )}
      </defs>

      {/* Main Large Star */}
      <path d="M12 2C12 8 8 12 2 12C8 12 12 16 12 22C12 16 16 12 22 12C16 12 12 8 12 2Z" fill={`url(#${gradientId})`} />
      {/* Medium Star (Bottom Left) */}
      <path d="M6.5 14C6.5 16.5 5 17.5 2.5 17.5C5 17.5 6.5 18.5 6.5 21C6.5 18.5 8 17.5 10.5 17.5C8 17.5 6.5 16.5 6.5 14Z" fill={`url(#${gradientId})`} />
      {/* Tiny Star (Top Left) */}
      <path d="M5 4C5 5.5 4.5 6 3 6C4.5 6 5 6.5 5 8C5 6.5 5.5 6 7 6C5.5 6 5 5.5 5 4Z" fill={`url(#${gradientId})`} />
    </svg>
  );
};

// --- TCO WORKBOOK COMPONENT ---
const TCOWorkbook = ({ data, messageId }) => {
  const storageKey = `workbook_page_${messageId}`;
  const [currentPage, setCurrentPage] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved ? parseInt(saved, 10) : 0;
  });

  const totalPages = data?.pages?.length || 0;
  const activePage = data?.pages?.[currentPage];
  const [animateIn, setAnimateIn] = useState(false);


  useEffect(() => {
    setAnimateIn(true);
  }, []);
  useEffect(() => {
    if (totalPages > 0) {
      localStorage.setItem(storageKey, currentPage.toString());
    }
  }, [currentPage, storageKey, totalPages]);

  useEffect(() => {
    if (totalPages > 0 && currentPage >= totalPages) {
      console.warn(
        `[TCOWorkbook] Page ${currentPage} out of bounds (max ${totalPages - 1}), resetting to 0`,
      );
      setCurrentPage(0);
    }
  }, [totalPages, currentPage]);

  if (
    !data ||
    !data.pages ||
    !Array.isArray(data.pages) ||
    data.pages.length === 0
  ) {
    return (
      <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/25 text-rose-200 text-xs">
        Data unavailable.
      </div>
    );
  }

  const handleNext = () =>
    setCurrentPage((p) => Math.min(totalPages - 1, p + 1));
  const handlePrev = () => setCurrentPage((p) => Math.max(0, p - 1));
  const goHome = () => setCurrentPage(0);

  const markdownComponents = {
    h1: ({ node, ...props }) => (
      <h1
        className="text-lg tracking-tight font-bold mb-3 mt-4 first:mt-0 text-accent-200 border-b border-accent-400/25 pb-2"
        {...props}
      />
    ),
    h2: ({ node, ...props }) => (
      <h2
        className="text-base font-bold mb-3 mt-4 border-b hairline pb-1"
        {...props}
      />
    ),
    h3: ({ node, ...props }) => (
      <h3
        className="text-sm font-semibold mb-2 mt-3 text-accent-200"
        {...props}
      />
    ),
    p: ({ node, ...props }) => <p className="mb-3 last:mb-0" {...props} />,
    ul: ({ node, ...props }) => (
      <ul className="list-disc list-outside mb-4 space-y-1.5 ml-4" {...props} />
    ),
    ol: ({ node, ...props }) => (
      <ol className="list-decimal list-outside mb-4 space-y-1.5 ml-4" {...props} />
    ),
    li: ({ node, ...props }) => <li className="pl-1" {...props} />,
    table: ({ node, ...props }) => (
      <div className="overflow-x-auto my-4 rounded-xl glass shadow-e1">
        <table
          className="min-w-full divide-y divide-white/[0.06] text-left text-sm"
          {...props}
        />
      </div>
    ),
    thead: ({ node, ...props }) => (
      <thead
        className="bg-white/[0.04] font-semibold text-slate-100"
        {...props}
      />
    ),
    tbody: ({ node, ...props }) => (
      <tbody className="divide-y divide-white/[0.05]" {...props} />
    ),
    tr: ({ node, ...props }) => (
      <tr
        className="hover:bg-accent-400/[0.06] transition-colors duration-150 group"
        {...props}
      />
    ),
    th: ({ node, ...props }) => (
      <th
        className="px-4 py-3 font-semibold whitespace-nowrap text-accent-200"
        {...props}
      />
    ),
    td: ({ node, ...props }) => (
      <td className="px-4 py-3 align-top text-slate-300" {...props} />
    ),
    code: ({ node, className, ...props }) => {
      const isBlock = /language-/.test(className || "");
      return isBlock ? (
        <code
          className="block px-4 py-3 bg-ink-950/60 rounded-lg font-sans text-xs overflow-x-auto border border-white/[0.06] my-3 text-slate-300"
          {...props}
        />
      ) : (
        <code
          className="px-1.5 py-0.5 bg-ink-950/60 rounded text-xs font-sans text-accent-200 border border-accent-400/20"
          {...props}
        />
      );
    },
    blockquote: ({ node, ...props }) => (
      <blockquote
        className="border-l-4 border-accent-400/50 pl-4 py-1 my-3 italic text-slate-400 bg-accent-400/[0.06] rounded-r"
        {...props}
      />
    ),
    hr: ({ node, ...props }) => (
      <hr className="my-6 hairline" {...props} />
    ),
  };

  return (
    <div className="flex flex-col w-full">
      {/* Page Content */}
      <div className="min-h-[150px] animate-in fade-in duration-300 prose prose-invert max-w-none">
        {activePage && activePage.content ? (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={markdownComponents}
          >
            {activePage.content}
          </ReactMarkdown>
        ) : (
          <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/25 text-amber-200 text-sm">
            Page {currentPage + 1} content not available.
            <button
              onClick={goHome}
              className="ml-2 underline hover:text-amber-100"
            >
              Return to Summary
            </button>
          </div>
        )}
      </div>

      {/* Navigation Controls */}
      <div className="mt-6 pt-3 border-t hairline flex items-center justify-between select-none">
        <button
          onClick={handlePrev}
          disabled={currentPage === 0}
          className={`flex items-center px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${currentPage === 0
            ? "text-slate-600 cursor-not-allowed"
            : "text-slate-300 hover:bg-white/[0.06] hover:text-white active:scale-95"
            }`}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </button>

        <div className="flex items-center space-x-2">
          <div className="hidden sm:flex space-x-1 mr-2">
            {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => (
              <div
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${i === currentPage || (i === 4 && currentPage >= 4)
                  ? "bg-accent-400"
                  : "bg-slate-600"
                  }`}
              />
            ))}
          </div>

          <span className="text-xs text-slate-400 font-sans">
            {currentPage === 0 ? "Summary View" : `Detail ${currentPage}`} of{" "}
            {totalPages - 1}
          </span>
        </div>

        <button
          onClick={handleNext}
          disabled={currentPage >= totalPages - 1}
          className={`flex items-center px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${currentPage >= totalPages - 1
            ? "text-slate-600 cursor-not-allowed"
            : "text-accent-200 hover:bg-accent-400/[0.1] hover:text-accent-100 active:scale-95"
            }`}
        >
          {currentPage === 0 ? "View Details" : "Next"}
          <ChevronRight className="w-4 h-4 ml-1" />
        </button>
      </div>

      {currentPage > 0 && (
        <button
          onClick={goHome}
          className="self-center mt-2 flex items-center text-[10px] text-slate-500 hover:text-accent-300 transition-colors"
        >
          <LayoutDashboard className="w-3 h-3 mr-1" />
          Back to Summary
        </button>
      )}
    </div>
  );
};

// --- FILE ATTACHMENT COMPONENT ---
const FileAttachment = ({ attachment, relatedFile, darkMode = true }) => {
  const [isLoading, setIsLoading] = useState(false);
  const file = attachment || relatedFile;

  if (!file) return null;

  // Message.jsx -> FileAttachment
  const handleViewFile = async () => {
    setIsLoading(true);
    try {
      // 1. Direct signed URL
      if (file.signed_url) {
        window.open(file.signed_url, "_blank");
        return;
      }
      // 2. Newly uploaded local blob URL (before server refresh)
      if (file.local_url) {
        window.open(file.local_url, "_blank");
        return;
      }
      // 3. Cloud storage path
      if (file.gcs_path) {
        const res = await axios.get(`${BACKEND_URL}/api/chat/attachment/url`, {
          params: { gcs_path: file.gcs_path },
        });
        if (res.data.signed_url) {
          window.open(res.data.signed_url, "_blank");
        } else {
          throw new Error("No signed URL returned");
        }
      }
    } catch (err) {
      console.error("File open error:", err);
      alert("Unable to open file. The link may have expired or the file is no longer available.");
    } finally {
      setIsLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const getFileIcon = (fileType) => {
    if (!fileType) return <FileText className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  return (
    <div
      className={`mt-3 p-3 rounded-xl transition-colors ${darkMode
        ? "glass hover:bg-white/[0.05]"
        : "bg-white/60 border border-white/80 shadow-sm hover:bg-white/90"
        }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 overflow-hidden flex-1">
          <div
            className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border ${darkMode
              ? "bg-accent-400/12 border-accent-400/25 text-accent-300"
              : "bg-sky-50 border-sky-200 text-sky-600"
              }`}
          >
            {getFileIcon(file.file_type)}
          </div>

          <div className="flex flex-col min-w-0 flex-1">
            <span className={`text-xs font-medium truncate ${darkMode ? "text-slate-200" : "text-slate-800"}`}>
              {file.file_name ||
                file.filename ||
                file.originalname ||
                "Attachment"}
            </span>

            <div className={`flex items-center gap-2 text-[10px] ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
              {file.gcs_path && (
                <span className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                  Stored Securely
                </span>
              )}
              {file.file_size && <span>{formatFileSize(file.file_size)}</span>}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={handleViewFile}
            disabled={isLoading}
            className={`p-1.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${darkMode
              ? "text-slate-400 hover:text-accent-300 hover:bg-white/[0.06]"
              : "text-slate-500 hover:text-sky-600 hover:bg-slate-100"
              }`}
            title="View Document"
          >
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-accent-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
          </button>

          <button
            onClick={handleViewFile}
            disabled={isLoading}
            className={`p-1.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${darkMode
              ? "text-slate-400 hover:text-emerald-300 hover:bg-white/[0.06]"
              : "text-slate-500 hover:text-emerald-600 hover:bg-slate-100"
              }`}
            title="Download"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// --- GENERATED FILE CARD COMPONENT ---
const GeneratedFileCard = ({ file, darkMode = true }) => {
  const [isZipLoading, setIsZipLoading] = useState(false);
  const [isExcelLoading, setIsExcelLoading] = useState(false);

  // Safely extract the new nested reports, or fallback to the old format
  const zipReport = file?.zip_report;
  const cumulativeReport = file?.cumulative_report;
  const isLegacyFormat = !zipReport && !cumulativeReport && file?.file_name;

  // --- Google Drive save (only for Google sign-in accounts) ---
  const [driveState, setDriveState] = useState("idle"); // idle | saving | saved | error
  const [driveResult, setDriveResult] = useState(null);
  const [driveError, setDriveError] = useState("");
  const [driveProgress, setDriveProgress] = useState({ done: 0, total: 0 });

  let currentUser = null;
  try {
    currentUser = JSON.parse(localStorage.getItem("user") || "null");
  } catch (_) {
    currentUser = null;
  }
  const isGoogleUser = currentUser?.auth_provider === "google";

  const driveFiles = [cumulativeReport, zipReport, isLegacyFormat ? file : null]
    .filter((r) => r && r.gcs_path)
    .map((r) => ({
      gcs_path: r.gcs_path,
      file_name: r.file_name || r.filename,
      file_type: r.file_type,
    }));

  const drivePct =
    driveProgress.total > 0
      ? Math.round((driveProgress.done / driveProgress.total) * 100)
      : 0;

  const handleSaveToDrive = async () => {
    if (!driveFiles.length) return;
    setDriveState("saving");
    setDriveError("");
    setDriveProgress({ done: 0, total: 0 });
    try {
      const data = await saveReportsToDrive(driveFiles, setDriveProgress);
      setDriveResult(data);
      setDriveState("saved");
    } catch (err) {
      console.error("Save to Drive error:", err);
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Could not save to Google Drive.";
      setDriveError(msg);
      setDriveState("error");
    }
  };

  const handleDownload = async (reportObj, setLoadingState) => {
    if (!reportObj) return;
    setLoadingState(true);
    try {
      let url = reportObj.signed_url || reportObj.download_link;

      if (!url && reportObj.gcs_path) {
        const res = await axios.get(`${BACKEND_URL}/api/chat/attachment/url`, {
          params: { gcs_path: reportObj.gcs_path },
        });
        url = res.data.signed_url;
      }

      if (url) {
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", reportObj.file_name || reportObj.filename || "download");
        link.setAttribute("target", "_blank");
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        throw new Error("Could not retrieve download link.");
      }
    } catch (err) {
      console.error("Download error:", err);
      alert("Could not download report. The file may have expired.");
    } finally {
      setLoadingState(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 mt-4 mb-2 w-full">

      {/* 1. MASTER TCO REPORT (EXCEL) */}
      {cumulativeReport && (
        <div
          className={`relative p-4 rounded-2xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 group transition-all ${darkMode
            ? "glass-sheen glass border-emerald-500/20 hover:bg-emerald-500/[0.06]"
            : "bg-white border-emerald-200 shadow-sm hover:bg-emerald-50/60"
            }`}
        >
          <div className="flex items-center gap-4">
            <div
              className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 border ${darkMode
                ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-300"
                : "bg-emerald-50 border-emerald-200 text-emerald-600"
                }`}
            >
              <FileSpreadsheet className="w-6 h-6" />
            </div>
            <div>
              <h4 className={`text-sm font-semibold mb-0.5 ${darkMode ? "text-emerald-50" : "text-slate-900"}`}>
                Executive Summary
              </h4>
              <p className={`text-xs font-sans ${darkMode ? "text-emerald-300/80" : "text-emerald-700"}`}>
                Unified Excel workbook with all mapped services and totals.
              </p>
            </div>
          </div>
          <button
            onClick={() => handleDownload(cumulativeReport, setIsExcelLoading)}
            disabled={isExcelLoading}
            className={`w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold rounded-xl transition-all ${darkMode
              ? "bg-emerald-500 hover:bg-emerald-400 active:bg-emerald-600 text-[#04140D] shadow-e1 disabled:opacity-70 disabled:cursor-not-allowed"
              : "bg-emerald-200 text-emerald-800 hover:bg-emerald-300 border border-emerald-400 shadow-sm"
              }`}
          >
            {isExcelLoading ? (
              <div className="w-3 h-3 border-2 border-current/40 border-t-current rounded-full animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Download Master Report
          </button>
        </div>
      )}

      {/* 2. DETAILED ANALYSIS (ZIP) */}
      {zipReport && (
        <div
          className={`relative p-4 rounded-2xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 group transition-all ${darkMode
            ? "glass-sheen glass border-blue-500/20 hover:bg-blue-500/[0.06]"
            : "bg-white border-blue-200 shadow-sm hover:bg-blue-50/60"
            }`}
        >
          <div className="flex items-center gap-4">
            <div
              className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 border ${darkMode
                ? "bg-blue-500/15 border-blue-500/30 text-blue-300"
                : "bg-blue-50 border-blue-200 text-blue-600"
                }`}
            >
              <Download className="w-6 h-6" />
            </div>
            <div>
              <h4 className={`text-sm font-semibold mb-0.5 ${darkMode ? "text-blue-50" : "text-slate-900"}`}>
                Detailed Analysis & Recommendations
              </h4>
              <p className={`text-xs font-sans ${darkMode ? "text-blue-300/80" : "text-blue-700"}`}>
                ZIP archive containing deep-dive reports for each service.
              </p>
            </div>
          </div>
          <button
            onClick={() => handleDownload(zipReport, setIsZipLoading)}
            disabled={isZipLoading}
            className={`w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold rounded-xl transition-all ${darkMode
              ? "bg-blue-500 hover:bg-blue-400 active:bg-blue-600 text-white shadow-e1 disabled:opacity-70 disabled:cursor-not-allowed"
              : "bg-blue-200 text-blue-800 hover:bg-blue-300 border border-blue-400 shadow-sm"
              }`}
          >
            {isZipLoading ? (
              <div className="w-3 h-3 border-2 border-current/40 border-t-current rounded-full animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Download Detailed Archive
          </button>
        </div>
      )}

      {/* 3. LEGACY FALLBACK */}
      {isLegacyFormat && (
        <div
          className={`relative p-4 rounded-2xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 group transition-all ${darkMode
            ? "glass-sheen glass border-emerald-500/20 hover:bg-emerald-500/[0.06]"
            : "bg-white border-emerald-200 shadow-sm hover:bg-emerald-50/60"
            }`}
        >
          <div className="flex items-center gap-4">
            <div
              className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 border ${darkMode
                ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-300"
                : "bg-emerald-50 border-emerald-200 text-emerald-600"
                }`}
            >
              <FileSpreadsheet className="w-6 h-6" />
            </div>
            <div>
              <h4 className={`text-sm font-semibold mb-0.5 ${darkMode ? "text-emerald-50" : "text-slate-900"}`}>
                TCO Analysis Report
              </h4>
              <p className={`text-xs font-sans ${darkMode ? "text-emerald-300/80" : "text-emerald-700"}`}>
                Archived TCO analysis document.
              </p>
            </div>
          </div>
          <button
            onClick={() => handleDownload(file, setIsZipLoading)}
            disabled={isZipLoading}
            className={`w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold rounded-xl transition-all ${darkMode
              ? "bg-emerald-500 hover:bg-emerald-400 active:bg-emerald-600 text-[#04140D] shadow-e1 disabled:opacity-70 disabled:cursor-not-allowed"
              : "bg-emerald-200 text-emerald-800 hover:bg-emerald-300 border border-emerald-400 shadow-sm"
              }`}
          >
            {isZipLoading ? (
              <div className="w-3 h-3 border-2 border-current/40 border-t-current rounded-full animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Download Report
          </button>
        </div>
      )}

      {/* SAVE TO GOOGLE DRIVE — Google sign-in accounts only */}
      {isGoogleUser && driveFiles.length > 0 && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-1 pt-1">
          <p className={`text-xs ${darkMode ? "text-slate-400" : "text-slate-500"}`}>
            {driveState === "saved"
              ? "Saved to the Google Drive linked to your account."
              : "Store a copy in the Google Drive linked to your account."}
          </p>
          {driveState === "saved" ? (
            <a
              href={
                driveResult?.folder_link ||
                driveResult?.files?.[0]?.web_view_link ||
                "#"
              }
              target="_blank"
              rel="noopener noreferrer"
              className={`w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 border text-xs font-semibold rounded-xl transition-all ${darkMode
                ? "bg-white/5 hover:bg-white/10 border-emerald-400/30 text-emerald-200"
                : "bg-emerald-50 hover:bg-emerald-100 border-emerald-300 text-emerald-700"
                }`}
            >
              <Check className="w-4 h-4" />
              Open in Drive
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          ) : (
            <button
              onClick={handleSaveToDrive}
              disabled={driveState === "saving"}
              className={`relative w-full sm:w-auto overflow-hidden flex items-center justify-center gap-2 px-4 py-2.5 border text-xs font-semibold rounded-xl transition-all disabled:opacity-70 disabled:cursor-not-allowed ${darkMode
                ? "bg-white/5 hover:bg-white/10 border-white/15 text-slate-100"
                : "bg-slate-50 hover:bg-slate-100 border-slate-300 text-slate-700"
                }`}
            >
              {driveState === "saving" && (
                <span
                  aria-hidden="true"
                  className={`pointer-events-none absolute inset-y-0 left-0 ${darkMode ? "bg-white/20" : "bg-slate-900/10"
                    } ${driveProgress.total > 0
                      ? "transition-[width] duration-300 ease-out"
                      : `w-1/3 bg-gradient-to-r from-transparent ${darkMode ? "via-white/35" : "via-slate-900/15"
                      } to-transparent animate-sweep motion-reduce:hidden`
                    }`}
                  style={
                    driveProgress.total > 0
                      ? { width: `${Math.max(drivePct, 6)}%` }
                      : undefined
                  }
                />
              )}
              {driveState === "saving" ? (
                <div className="relative w-3 h-3 border-2 border-current/40 border-t-current rounded-full animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              <span className="relative tabular-nums">
                {driveState === "saving"
                  ? driveProgress.total > 0
                    ? `Saving… ${drivePct}% (${driveProgress.done}/${driveProgress.total})`
                    : "Saving to Drive…"
                  : "Save all Files to Google Drive"}
              </span>
            </button>
          )}
        </div>
      )}
      {driveState === "error" && (
        <p className={`text-xs px-1 ${darkMode ? "text-red-300" : "text-red-600"}`}>
          {driveError}{" "}
          <button
            onClick={handleSaveToDrive}
            className={`underline ${darkMode ? "hover:text-red-200" : "hover:text-red-700"}`}
          >
            Retry
          </button>
        </p>
      )}
    </div>
  );
};

// --- TCO DASHBOARD CARD (opens the visual dashboard) ---
const DashboardCard = ({ chartData, darkMode = true }) => {
  const [open, setOpen] = useState(false);

  // 1. Extract raw totals
  const rawTotals = chartData?.totals || {};
  const totalAws = rawTotals.total_aws_cost || 0;
  const totalGcp = rawTotals.total_gcp_cost || 0;

  // 2. Calculate the exact same way DashboardModal does
  let grandTotalVal = rawTotals.invoice_grand_total;
  if (grandTotalVal === 0 || grandTotalVal == null) {
    grandTotalVal = rawTotals.invoice_total_savings ?? totalAws;
  }

  // 3. Calculate raw savings, but cap it at 0 so it never goes negative
  const rawSavings = grandTotalVal - totalGcp;
  const displaySavings = Math.max(0, rawSavings);

  const fmt = (n) => `$${Math.round(n || 0).toLocaleString("en-US")}`;

  return (
    <>
      <div
        className={`relative mt-3 mb-2 p-4 rounded-2xl border flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between group transition-all ${darkMode
          ? "glass-sheen glass border-accent-400/20 hover:bg-accent-400/[0.06]"
          : "bg-white border-sky-200 shadow-sm hover:bg-sky-50/60"
          }`}
      >
        <div className="flex items-center gap-4">
          <div
            className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 border ${darkMode
              ? "bg-accent-400/12 border-accent-400/30 text-accent-200"
              : "bg-sky-50 border-sky-200 text-sky-600"
              }`}
          >
            <LayoutDashboard className="w-6 h-6" />
          </div>
          <div>
            <h4 className={`text-sm font-semibold mb-0.5 ${darkMode ? "text-accent-50" : "text-slate-900"}`}>
              TCO Dashboard
            </h4>
            <p className={`text-xs font-sans ${darkMode ? "text-slate-400" : "text-slate-500"}`}>
              {rawTotals.total_services_mapped || chartData?.services?.length || 0} services •{" "}
              {/* Always display "Saves" and floor the value at 0 */}
              <span className={darkMode ? "text-emerald-300" : "text-emerald-600"}>
                Saves {fmt(displaySavings)}
              </span>
            </p>
          </div>
        </div>
        <button
          onClick={() => setOpen(true)}
          className={`w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold rounded-xl transition-all ${darkMode ? "bg-accent-gradient hover:brightness-110 active:brightness-95 text-[#04121F] shadow-glow-sm" : "bg-sky-200 text-sky-800 border border-sky-400 hover:bg-sky-300 shadow-sm"}`}
        >
          <Eye className="w-4 h-4" />
          View Insights
        </button>
      </div>

      {open && (
        <DashboardModal chartData={chartData} onClose={() => setOpen(false)} darkMode={darkMode} />
      )}
    </>
  );
};

// --- MAIN MESSAGE COMPONENT ---
const Message = ({ message, index, progressSteps, showToast, darkMode, onEditAndRerun, isSendDisabled }) => {
  const isUser = message.type === "user" || message.sender === "user";
  const isBot = message.type === "bot" || message.sender === "bot";
  const isError = message.type === "error";
  const showtoast = showToast || (() => { });
  const [showFeedbackReasons, setShowFeedbackReasons] = useState(false);
  const [feedbackReason, setFeedbackReason] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(message.content || message.text || "");
  const editTextareaRef = useRef(null);

  useEffect(() => {
    if (isEditing && editTextareaRef.current) {
      const el = editTextareaRef.current;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    setEditText(message.content || message.text || "");
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditText(message.content || message.text || "");
    setIsEditing(false);
  };

  const handleSaveEdit = () => {
    const trimmed = editText.trim();
    if (!trimmed || isSendDisabled) return;
    setIsEditing(false);
    onEditAndRerun?.(trimmed);
  };
  if (isBot && message.isTyping) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex w-full mb-6 justify-start"
      >
        <div className="w-full max-w-[80%] md:max-w-[60%]">
          <div
            className={`relative rounded-2xl p-4 shadow-e1 ${darkMode ? "glass-sheen glass" : "bg-white border border-slate-200"
              }`}
          >
            {(!progressSteps || progressSteps.length === 0) && (
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center shrink-0">
                  <AISparkleIcon darkMode={darkMode} className="w-6 h-6 animate-pulse" />
                </div>

                <div
                  className={`text-[15px] font-sans italic tracking-wide animate-pulse ml-1 ${darkMode ? "text-accent-200" : "text-sky-700"
                    }`}
                >
                  Processing your request
                  <span className="inline-flex ml-0.5">
                    <span className="animate-bounce [animation-delay:-0.3s]">.</span>
                    <span className="animate-bounce [animation-delay:-0.15s]">.</span>
                    <span className="animate-bounce">.</span>
                  </span>
                </div>
              </div>
            )}

            {progressSteps && progressSteps.length > 0 && (
              <ProgressPanel progressSteps={progressSteps} embedded={true} darkMode={darkMode} />
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  const messageId = message.id || message.timestamp || `msg-${index}`;
  const isWorkbook =
    message.pricing_data && message.pricing_data.type === "workbook";
  const hasAttachment = message.attachment || message.attachment_data;
  const hasRelatedFile = message.related_file;
  const hasGeneratedFile = message.generated_file;
  const hasChartData =
    message.chart_data &&
    Array.isArray(message.chart_data.services) &&
    message.chart_data.services.length > 0;

  const getMessageClass = () => {
    if (isUser)
      return darkMode
        ? "bg-gradient-to-br from-accent-500 to-accent-600 text-white shadow-glow-sm"
        : "bg-sky-200 border border-sky-300 text-slate-900";
    if (isError)
      return "bg-rose-500/12 text-rose-100 border border-rose-500/30";
    return darkMode
      ? "glass glass-sheen text-slate-100 w-full"
      : "bg-white border border-slate-200 shadow-sm text-slate-900 w-full";
  };

  const getIcon = () => {
    if (isUser) return <User className="w-5 h-5" />;
    if (isError) return <AlertCircle className="w-5 h-5 text-red-400" />;
    // Return your custom sleek AI sparkle instead of the robot!
    return <AISparkleIcon darkMode={darkMode} className="w-5 h-5" />;
  };

  const copyToClipboard = () => {
    const textToCopy = isWorkbook
      ? JSON.stringify(message.pricing_data, null, 2)
      : message.content || message.text;
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        showToast?.(
          "Copied to clipboard",
          "info"
        );
      })
      .catch(() => {
        showToast?.(
          "Failed to copy",
          "error"
        );
      });
  };

  const standardComponents = {
    h1: ({ node, ...props }) => (
      <h1 className="text-lg tracking-tight font-bold mb-2 mt-3 first:mt-0" {...props} />
    ),
    h2: ({ node, ...props }) => (
      <h2 className="text-base font-medium mb-2 mt-3" {...props} />
    ),
    h3: ({ node, ...props }) => (
      <h3 className="text-sm font-medium mb-1 mt-2" {...props} />
    ),
    p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
    ul: ({ node, ...props }) => (
      <ul className="list-disc list-inside mb-2 space-y-1" {...props} />
    ),
    ol: ({ node, ...props }) => (
      <ol className="list-decimal list-inside mb-2 space-y-1" {...props} />
    ),
    li: ({ node, ...props }) => <li className="ml-2" {...props} />,
    table: ({ node, ...props }) => (
      <div
        className={`overflow-x-auto my-3 rounded-xl ${darkMode ? "glass" : "bg-white border border-slate-200 shadow-sm"
          }`}
      >
        <table
          className="min-w-full border-collapse text-left text-sm"
          {...props}
        />
      </div>
    ),
    thead: ({ node, ...props }) => (
      <thead className={darkMode ? "bg-white/[0.04]" : "bg-slate-50"} {...props} />
    ),
    tbody: ({ node, ...props }) => (
      <tbody
        className={darkMode ? "divide-y divide-white/[0.05]" : "divide-y divide-slate-200"}
        {...props}
      />
    ),
    tr: ({ node, ...props }) => (
      <tr className="hover:bg-accent-400/[0.06] transition-colors" {...props} />
    ),
    th: ({ node, ...props }) => (
      <th
        className={`px-3 py-2 text-left text-xs font-semibold whitespace-nowrap ${darkMode ? "text-accent-200" : "text-sky-700"
          }`}
        {...props}
      />
    ),
    td: ({ node, ...props }) => (
      <td
        className={`px-3 py-2 text-sm align-top ${darkMode ? "text-slate-300" : "text-slate-700"}`}
        {...props}
      />
    ),
    code: ({ node, className, ...props }) => {
      // react-markdown v10 no longer passes `inline`; fenced code carries a
      // `language-*` class, so treat everything else as inline.
      const isBlock = /language-/.test(className || "");
      return isBlock ? (
        <code
          className={`block px-3 py-2 rounded-lg font-sans text-xs overflow-x-auto ${darkMode ? "bg-ink-950/60 text-slate-300" : "bg-slate-50 text-slate-800"
            }`}
          {...props}
        />
      ) : (
        <code
          className={`px-1.5 py-0.5 border rounded text-xs font-sans ${darkMode
            ? "bg-ink-950/60 border-accent-400/20 text-accent-200"
            : "bg-slate-100 border-slate-200 text-slate-800"
            }`}
          {...props}
        />
      );
    },
    pre: ({ node, ...props }) => (
      <pre
        className={`my-2 border rounded-lg overflow-x-auto ${darkMode ? "bg-ink-950/60 border-white/[0.06]" : "bg-slate-50 border-slate-200"
          }`}
        {...props}
      />
    ),
    blockquote: ({ node, ...props }) => (
      <blockquote
        className={`border-l-4 pl-3 py-1 my-2 italic rounded-r ${darkMode
          ? "border-accent-400/50 text-slate-400 bg-accent-400/[0.06]"
          : "border-sky-400/50 text-slate-600 bg-sky-50"
          }`}
        {...props}
      />
    ),
    hr: ({ node, ...props }) => (
      <hr className={`my-3 ${darkMode ? "hairline" : "border-slate-200"}`} {...props} />
    ),
    strong: ({ node, ...props }) => (
      <strong className={`font-semibold ${darkMode ? "text-white" : "text-slate-900"}`} {...props} />
    ),
    em: ({ node, ...props }) => (
      <em className={`italic ${darkMode ? "text-slate-200" : "text-slate-600"}`} {...props} />
    ),
  };
  const feedbackReasons = [
    "Incorrect answer",
    "Incomplete answer",
    "Not relevant",
    "Formatting issue",
    "Hallucinated information",
    "Other",
  ];
  return (
    <div
      className={`
    flex
    w-full
    mb-6
    message-enter
    ${isUser ? "justify-end" : "justify-start"}
  `}
    >
      <div
        className={`${isWorkbook
          ? "w-full max-w-4xl"
          : isUser
            ? "max-w-[70%] md:max-w-[60%]"
            : "max-w-[90%] md:max-w-[85%]"
          } ${isUser ? "order-2" : "order-1"}`}
      >
        <div
          className={`relative rounded-2xl ${isUser ? "px-4 py-3" : "px-4 py-4 md:px-5"
            } shadow-e1 transition-colors ${getMessageClass()}`}
        >
          {/* Message Header */}
          {!isUser && (
            <div className={`flex items-center space-x-2 mb-3 pb-2 border-b ${darkMode ? "hairline" : "border-slate-200"}`}>
              <div className="flex items-center justify-center shrink-0">
                {getIcon()}
              </div>

              <span
                className={`text-sm font-sans font-semibold tracking-wide ${darkMode ? "text-accent-200" : "text-sky-700"
                  }`}
              >
                Cost<span className="text-gradient-google">IQ</span> Assistant
                <span className={`${darkMode
                  ? "text-slate-400"
                  : "text-slate-800"
                  } text-xs font-sans tracking-normal font-normal ml-1.5`}>
                  •{" "}
                  {message.timestamp
                    ? new Date(message.timestamp.endsWith('Z') ? message.timestamp : message.timestamp + 'Z').toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                    : "now"}
                </span>
              </span>
            </div>
          )}


          {/* BULLETPROOF WRAPPER: CSS Grid completely prevents layout tearing/overlap */}
          <div className="grid grid-cols-1 gap-5 w-full">
            {/* Row 1: Progress Panel - Only shown if message has no content yet */}
            {isBot &&
              progressSteps &&
              progressSteps.length > 0 &&
              (!message.content || message.content.length === 0) && (
                <div className="w-full row-start-1 h-auto min-h-min">
                  <ProgressPanel
                    progressSteps={progressSteps}
                    embedded={true}
                    darkMode={darkMode}
                  />
                </div>
              )}

            {/* Row 2: Message Content */}
            <div className={`text-[15px] ${isUser ? (darkMode ? "text-white" : "text-slate-800") : darkMode ? "text-slate-200" : "text-slate-800"} font-sans leading-relaxed font-normal overflow-x-auto w-full row-start-2 h-auto min-h-min`}>
              {isUser && isEditing ? (
                <div className="flex flex-col gap-2 w-full">
                  <textarea
                    ref={editTextareaRef}
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSaveEdit();
                      } else if (e.key === "Escape") {
                        handleCancelEdit();
                      }
                    }}
                    rows={Math.min(8, Math.max(2, editText.split("\n").length))}
                    className="w-full resize-none rounded-xl bg-white/10 border border-white/25 px-3 py-2 text-[15px] text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-white/50"
                  />
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={handleCancelEdit}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white/80 hover:bg-white/10 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      disabled={!editText.trim() || isSendDisabled}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white text-accent-600 hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Check className="w-3.5 h-3.5" />
                      Save &amp; Submit
                    </button>
                  </div>
                </div>
              ) : isWorkbook ? (
                <TCOWorkbook
                  data={message.pricing_data}
                  messageId={messageId}
                />
              ) : (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={standardComponents}
                >
                  {message.content || message.text || ""}
                </ReactMarkdown>
              )}
            </div>
          </div>

          {/* Standard File Attachments */}
          {hasAttachment && (
            <FileAttachment
              attachment={message.attachment}
              relatedFile={message.attachment_data}
              darkMode={darkMode}
            />
          )}

          {/* Related Files */}
          {hasRelatedFile && (
            <FileAttachment relatedFile={message.related_file} darkMode={darkMode} />
          )}

          {/* Visual TCO Dashboard */}
          {hasChartData && (
            <DashboardCard chartData={message.chart_data} darkMode={darkMode} />
          )}

          {/* Generated Excel Report Card */}
          {hasGeneratedFile && (
            <GeneratedFileCard file={message.generated_file} darkMode={darkMode} />
          )}

          {/* Footer */}
          {isUser && (
            <div className="flex justify-end mt-1">
              <span className={`${darkMode
                ? "text-slate-400"
                : "text-slate-800"
                } text-xs font-sans tracking-normal font-normal ml-1.5`}>
                {message.timestamp
                  ? new Date(message.timestamp).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                  : "now"}
              </span>
            </div>
          )}

        </div>

        {/* Action Buttons */}
        {!isUser && !isError && (
          <div className="flex items-center space-x-1 mt-1.5 ml-1 transition-opacity duration-200">
            <button
              onClick={copyToClipboard}
              className="p-1.5 text-slate-500 hover:text-accent-300 hover:bg-white/[0.06] rounded-lg transition-all duration-200"
              title="Copy"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
            <div className="h-3 w-px bg-white/10 mx-1" />
            <button
              className="p-1.5 text-slate-500 hover:text-emerald-400 hover:bg-white/[0.06] rounded-lg transition-all duration-200"
              title="Good response"
            >
              <ThumbsUp className="w-3.5 h-3.5" />
            </button>
            <button className="p-1.5 text-slate-500 hover:text-rose-400 hover:bg-white/[0.06] rounded-lg transition-all duration-200" onClick={() => setShowFeedbackReasons(true)}
              aria-expanded={showFeedbackReasons}
              title="Bad response">
              <ThumbsDown className="w-3.5 h-3.5" />
            </button>
            {showFeedbackReasons && (
              <div className="mt-3 space-y-2">

                <select
                  autoFocus
                  value={feedbackReason}
                  onChange={(e) => setFeedbackReason(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setShowFeedbackReasons(false);
                    }
                  }}
                  className="
        w-full
        rounded-xl
        bg-ink-800
        border
        border-white/10
        px-3
        py-2
        text-sm
        text-white
        focus:outline-none
        focus:ring-2
        focus:ring-accent-400/50
      "
                >
                  <option value="">Why wasn't this helpful?</option>

                  {feedbackReasons.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
                <div className="flex justify-end gap-2">

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowFeedbackReasons(false);
                      setFeedbackReason("");
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    size="sm"
                    disabled={!feedbackReason}
                    onClick={() => {
                      console.log("Feedback reason:", feedbackReason);

                      alert("Feedback recorded");

                      setShowFeedbackReasons(false);
                      setFeedbackReason("");
                    }}
                  >
                    Submit
                  </Button>

                </div>

              </div>
            )}

          </div>
        )}

        {/* Edit action — user messages only */}
        {isUser && !isEditing && (
          <div className="flex items-center justify-end mt-1.5 mr-1">
            <button
              onClick={handleStartEdit}
              disabled={isSendDisabled}
              title="Edit and resend"
              className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${darkMode
                ? "text-slate-500 hover:text-accent-300 hover:bg-white/[0.06]"
                : "text-slate-500 hover:text-sky-600 hover:bg-slate-100"
                }`}
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Message;
